import type { VerifyEmailCodeResult } from "@iec-id/shared/contracts/auth";
import { Button, LinkButton, notify, OtpInput, useOnline } from "@iec-id/ui";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
	type AuthSearch,
	clearPendingSignIn,
	localUrl,
	type PendingSignIn,
	resolveService,
	resolveSignedInDestination,
	savePendingSignIn,
} from "@/lib/auth-flow";
import { api } from "@/lib/trpc";
import { AuthCard, AuthHeading, BackLink, OfflineNotice } from "./auth-card";
import { useSigninLocale, useSigninT } from "./signin-i18n";
import { useCountdown } from "./use-countdown";

const CODE_LENGTH = 6;

/**
 * Экран ввода кода (макет 4a, карточки 2 и 4).
 *
 * Один экран, четыре исхода: код подошёл, код не подошёл (с остатком попыток),
 * попытки кончились, код протух. Все четыре приходят из одного `state` в ответе
 * `auth.verifyEmailCode` — разбираем по нему, а не по набору полей.
 */
export function CodeStep({ search, pending }: { search: AuthSearch; pending: PendingSignIn }) {
	const t = useSigninT();
	const locale = useSigninLocale();
	const online = useOnline();
	const navigate = useNavigate();
	const service = resolveService(search);

	const [code, setCode] = useState("");
	const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
	const [failure, setFailure] = useState<string | null>(null);
	const [lockedUntil, setLockedUntil] = useState<number | null>(null);
	const [resendAt, setResendAt] = useState(pending.resendAt);
	const [expiresAt, setExpiresAt] = useState<number | null>(pending.expiresAt);
	const [handingOff, setHandingOff] = useState(false);

	const lockLeft = useCountdown(lockedUntil);
	const resendLeft = useCountdown(resendAt);
	const expiresLeft = useCountdown(expiresAt);

	/**
	 * Блокировка — это ЭКРАН, а не только таймер.
	 *
	 * Раньше признак был один (`lockedUntil !== null && lockLeft > 0`), и по
	 * истечении отсчёта экран молча возвращался к обычному виду — а кнопка
	 * «Новый код» на нём всё это время была нарисована выключенной и не
	 * нажималась ни разу (аудит 2026-08-20, К4). Теперь экран блокировки живёт,
	 * пока человек не запросит новый код: `lockLeft` гасит только ввод и саму
	 * кнопку «Новый код», а по нулю она становится живой. Поле ввода остаётся
	 * выключенным до нового кода: попытки по прежнему уже исчерпаны.
	 */
	const lockScreen = lockedUntil !== null;
	// Срок неизвестен (`null`) — значит письмо ушло не от нас (429): считать
	// такой код протухшим нельзя, он вполне может работать.
	const expired = expiresAt !== null && expiresLeft === 0;
	/** Сколько ещё ждать до «Новый код»: и снятия блокировки, и троттлинга. */
	const waitLeft = Math.max(lockLeft, resendLeft);

	/**
	 * Успешный вход уводит с SPA целиком, а не роутером.
	 *
	 * Cookie сессии появилась только что: перезагрузка страницы — самый дешёвый
	 * способ гарантировать, что ни один кэш TanStack Query не переживёт смену
	 * личности. Заодно это развязывает нас с `/onboarding`, который живёт в
	 * соседней ветке маршрутов.
	 */
	const finishSignIn = async (result: Extract<VerifyEmailCodeResult, { state: "signed-in" }>) => {
		clearPendingSignIn();
		setHandingOff(true);

		if (result.needsOnboarding) {
			window.location.assign(localUrl("/onboarding", search));
			return;
		}
		// Сессия есть, но кода авторизации у сервиса ещё нет — доигрываем OAuth
		// тихим запросом (research §3.2), иначе человек вернётся в сервис гостем.
		// Ответ сервера (`result.redirectTo`) идёт ПОСЛЕ него, а не до: сервер не
		// видит OAuth-контекста вкладки и без `serviceId` честно называет витрину.
		window.location.assign(await resolveSignedInDestination(search, result.redirectTo));
	};

	const verify = api.auth.verifyEmailCode.useMutation({
		onSuccess: (result) => {
			setFailure(null);
			switch (result.state) {
				case "signed-in":
					void finishSignIn(result);
					return;
				case "invalid":
					setAttemptsLeft(result.attemptsLeft);
					setCode("");
					return;
				case "locked":
					setLockedUntil(Date.now() + result.retryAfterSec * 1000);
					setCode("");
					notify.error(t("code.locked.title"), { description: t("code.locked.description") });
					return;
			}
		},
		onError: () => {
			setCode("");
			setFailure(t("code.failed"));
		},
	});

	const resend = api.auth.requestEmailCode.useMutation({
		onSuccess: (data) => {
			const now = Date.now();
			const next = {
				email: pending.email,
				maskedEmail: data.maskedEmail,
				expiresAt: now + data.expiresInSec * 1000,
				resendAt: now + data.resendAfterSec * 1000,
			};
			savePendingSignIn(next);
			setResendAt(next.resendAt);
			setExpiresAt(next.expiresAt);
			setAttemptsLeft(null);
			setLockedUntil(null);
			setFailure(null);
			setCode("");
			notify.info(t("code.resent"));
		},
		onError: () => setFailure(t("email.errors.failed")),
	});

	const submit = (value: string) => {
		if (value.length !== CODE_LENGTH || lockScreen || expired || verify.isPending) return;
		verify.mutate({
			email: pending.email,
			code: value,
			purpose: "sign_in",
			rememberDevice: false,
			...(service ? { serviceId: service.id } : {}),
			// Код мог быть запрошен в одной вкладке, а введён в другой: сервер
			// берёт тот реферальный код, что дошёл — сохранённый с письмом либо
			// этот (контракт `VerifyEmailCodeInput.referralCode`).
			...(search.ref ? { referralCode: search.ref } : {}),
		});
	};

	const sendAgain = () => {
		if (waitLeft > 0 || resend.isPending || !online) return;
		resend.mutate({
			email: pending.email,
			purpose: "sign_in",
			locale,
			...(service ? { serviceId: service.id } : {}),
			...(search.ref ? { referralCode: search.ref } : {}),
		});
	};

	const busy = verify.isPending || resend.isPending || handingOff;
	const errorText = expired
		? t("code.expired")
		: (failure ??
			(attemptsLeft !== null ? t("code.mismatch", { count: attemptsLeft }) : undefined));

	return (
		<AuthCard>
			<BackLink
				onClick={() => {
					clearPendingSignIn();
					void navigate({ to: "/", search });
				}}
			/>
			<AuthHeading
				title={t("code.title")}
				subtitle={t("code.subtitle", { email: pending.email })}
			/>

			<OfflineNotice />

			<OtpInput
				length={CODE_LENGTH}
				label={t("code.label")}
				value={code}
				onChange={(value) => {
					setCode(value);
					if (value.length > 0) setFailure(null);
				}}
				onComplete={submit}
				hasError={Boolean(errorText)}
				{...(errorText ? { errorText } : {})}
				// После блокировки старый код мёртв: попытки по нему исчерпаны, и
				// поле остаётся выключенным до нового кода, а не до конца отсчёта.
				disabled={busy || lockScreen}
				// Фокус приходит на ячейки сразу: человек только что переключился
				// из почтового клиента и держит код в буфере обмена.
				autoFocus
			/>

			{lockScreen ? (
				<>
					<p role="status" aria-live="polite" className="t-meta text-fg-muted">
						{waitLeft > 0 ? t("code.locked.inline", { seconds: waitLeft }) : t("code.locked.ready")}
					</p>
					<Button
						variant="second"
						fullWidth
						state={
							resend.isPending
								? "loading"
								: waitLeft > 0 || !online
									? "disabled"
									: // Ради этой ветки экран блокировки и не исчезает по таймеру:
										// иначе единственное действие на экране отказа недостижимо.
										"default"
						}
						onClick={sendAgain}
					>
						{t("code.newCode")}
					</Button>
				</>
			) : (
				<>
					<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
						<span className="t-meta text-fg-muted">
							{expired
								? t("code.expired")
								: expiresAt === null
									? t("code.validUnknown")
									: t("code.validFor", { minutes: Math.max(1, Math.ceil(expiresLeft / 60)) })}
						</span>
						<LinkButton
							onClick={sendAgain}
							disabled={resendLeft > 0 || resend.isPending || !online}
						>
							{resendLeft > 0 ? t("code.resendIn", { seconds: resendLeft }) : t("code.resend")}
						</LinkButton>
					</div>
					<Button
						variant="primary"
						fullWidth
						state={
							verify.isPending || handingOff
								? "loading"
								: code.length === CODE_LENGTH && !expired && online
									? "default"
									: "disabled"
						}
						onClick={() => submit(code)}
					>
						{t("code.submit")}
					</Button>
				</>
			)}
		</AuthCard>
	);
}
